import {
  GET_AVAILABLE_CALENDARS_BY_GOOGLE_SOCIAL_ACCOUNT_ID,
  GET_AVAILABLE_CALENDARS_BY_MICROSOFT_SOCIAL_ACCOUNT_ID,
  GET_GOOGLE_ACCESS_TOKEN,
  LIST_SOCIAL_ACCOUNTS
} from '@/graphql/queries'
import {
  LINK_SOCIAL_ACCOUNT_CANVA,
  LINK_SOCIAL_ACCOUNT_DROPBOX,
  LINK_SOCIAL_ACCOUNT_GOOGLE,
  LINK_SOCIAL_ACCOUNT_INSTAGRAM,
  LINK_SOCIAL_ACCOUNT_MICROSOFT,
  LINK_SOCIAL_ACCOUNT_TWITTER,
  UNLINK_SOCIAL_ACCOUNT,
  LINK_SOCIAL_ACCOUNT_POWERBI
} from '@/graphql/mutations'
import { handleActionError } from '@/helpers/ErrorHandler'
import { apolloCall } from '@/helpers/Graphql'

const ACCOUNT_TYPES = {
  TWITTER: 'TWITTER',
  INSTAGRAM: 'INSTAGRAM',
  GOOGLE: 'GOOGLE',
  MICROSOFT: 'MICROSOFT',
  DROPBOX: 'DROPBOX',
  POWER_BI: 'POWER_BI'
}

const OAUTH_URLS = {
  twitter: process.env.VUE_APP_TWITTER_OAUTH_LINK,
  instagram: process.env.VUE_APP_INSTAGRAM_OAUTH_LINK,
  google: process.env.VUE_APP_GOOGLE_OAUTH_LINK,
  microsoft: process.env.VUE_APP_MICROSOFT_OAUTH_LINK,
  dropbox: process.env.VUE_APP_DROPBOX_OAUTH_LINK,
  powerbi: process.env.VUE_APP_POWERBI_OAUTH_LINK
}

const getSocialAccountName = ({ type, info }) => {
  switch (type) {
    case ACCOUNT_TYPES.TWITTER:
      return info?.screen_name || info?.name
    case ACCOUNT_TYPES.INSTAGRAM:
      return info?.username || info?.name
    case ACCOUNT_TYPES.GOOGLE:
      return info?.name || info?.given_name
    case ACCOUNT_TYPES.MICROSOFT:
      return info?.name || info?.username || 'Unknown Account'
    case ACCOUNT_TYPES.DROPBOX:
      return info?.display_name || info?.given_name
    case ACCOUNT_TYPES.POWER_BI:
      return info?.name || info?.username || 'Unknown Account'
    default:
      return info?.name || 'Unknown Account'
  }
}

const normalizeSocialAccountScopes = ({ type, info }) => {
  const scopes = {
    drive: false,
    calendar: false
  }

  switch (type) {
    case ACCOUNT_TYPES.GOOGLE:
      scopes.drive = info?.scopes?.includes('https://www.googleapis.com/auth/drive.file')
      scopes.calendar = info?.scopes?.includes('https://www.googleapis.com/auth/calendar.readonly')
      break
    case ACCOUNT_TYPES.MICROSOFT:
      scopes.drive = info?.scopes?.includes('Files.Read.All')
      scopes.calendar = info?.scopes?.includes('Calendars.Read')
      break
    case ACCOUNT_TYPES.DROPBOX:
      scopes.drive = info?.scopes?.includes('files.content.read')
      break
  }
  return scopes
}

const toStandard = (sa) => {
  return ({
    id: sa.id,
    type: sa.type,
    name: getSocialAccountName(sa),
    error: sa.info?.message,
    scopes: normalizeSocialAccountScopes(sa)
  })
}

const openSocialLoginWindow = (url, platform, action, closeFn) => {
  const authWindow = window.open(url, 'kitcast', 'height=600,width=800,toolbar=0')
  const interval = setInterval(() => {
    if (!authWindow.closed) {
      authWindow.postMessage({ platform, action: action || 'link' }, process.env.VUE_APP_DEFAULT_URL)
    } else {
      clearInterval(interval)
      closeFn?.()
    }
  }, 500)
  authWindow?.focus?.()
}

const handleSocialMessage = (data) => {
  const {
    action,
    platform,
    oauth_token: oauthToken,
    oauth_verifier: oauthVerifier,
    code
  } = data

  if (!action || !platform) return null

  const platformConfig = {
    twitter: {
      input: { oauthToken, oauthVerifier },
      action: 'linkTwitterAccount',
      validate: input => input.oauthToken && input.oauthVerifier
    },
    instagram: {
      input: { code },
      action: 'linkInstagramAccount',
      validate: input => input.code
    },
    google: {
      input: { code },
      action: 'linkGoogleAccount',
      validate: input => input.code
    },
    microsoft: {
      input: { code },
      action: 'linkMicrosoftAccount',
      validate: input => input.code
    },
    dropbox: {
      input: { code },
      action: 'linkDropboxAccount',
      validate: input => input.code
    },
    powerbi: {
      input: { code },
      action: 'linkPowerBIAccount',
      validate: input => input.code
    }
  }

  const config = platformConfig[platform]
  if (!config) return null

  if (!config.validate(config.input)) return null

  return {
    input: config.input,
    action: config.action
  }
}

export default {
  namespaced: true,
  state: {
    socialAccounts: [],
    loading: {
      socialAccounts: true
    }
  },
  actions: {
    async getSocialAccounts ({ commit }) {
      try {
        const { listSocialAccounts } = await apolloCall({
          commit,
          query: LIST_SOCIAL_ACCOUNTS,
          key: 'socialAccounts'
        })
        commit('SET_SOCIAL_ACCOUNTS', listSocialAccounts)
      } catch (e) {
        handleActionError(e)
      }
    },
    async linkSocialAccountCanva (_, { token, nonce }) {
      await apolloCall({
        mutation: LINK_SOCIAL_ACCOUNT_CANVA,
        variables: { input: { token }, nonce }
      })
    },
    async linkTwitterAccount ({ commit }, payload) {
      const { linkSocialAccountTwitter } = await apolloCall({
        mutation: LINK_SOCIAL_ACCOUNT_TWITTER,
        variables: { input: payload }
      })
      commit('ADD_SOCIAL_ACCOUNT', [linkSocialAccountTwitter])
    },
    async linkInstagramAccount ({ commit }, payload) {
      const { linkSocialAccountsInstagram } = await apolloCall({
        mutation: LINK_SOCIAL_ACCOUNT_INSTAGRAM,
        variables: { input: payload }
      })
      commit('ADD_SOCIAL_ACCOUNT', linkSocialAccountsInstagram)
    },
    async linkGoogleAccount ({ commit }, payload) {
      const { linkSocialAccountGoogle } = await apolloCall({
        mutation: LINK_SOCIAL_ACCOUNT_GOOGLE,
        variables: { input: payload }
      })
      commit('ADD_SOCIAL_ACCOUNT', [linkSocialAccountGoogle])
    },
    async getGoogleAccountAccessToken (_, socialAccountId) {
      try {
        const { getSocialAccountGoogleAccessToken: accessToken } = await apolloCall({
          query: GET_GOOGLE_ACCESS_TOKEN,
          variables: { socialAccountId }
        })
        return accessToken
      } catch (e) {
        handleActionError(e)
      }
    },
    async linkMicrosoftAccount ({ commit }, payload) {
      const { linkSocialAccountMicrosoft } = await apolloCall({
        mutation: LINK_SOCIAL_ACCOUNT_MICROSOFT,
        variables: { input: payload }
      })
      commit('ADD_SOCIAL_ACCOUNT', [linkSocialAccountMicrosoft])
    },
    async linkDropboxAccount ({ commit }, payload) {
      const { linkSocialAccountDropbox } = await apolloCall({
        mutation: LINK_SOCIAL_ACCOUNT_DROPBOX,
        variables: { input: payload }
      })
      commit('ADD_SOCIAL_ACCOUNT', [linkSocialAccountDropbox])
    },
    async linkPowerBIAccount({ commit }, input) {
      const { linkSocialAccountPowerBI } = await apolloCall({
        mutation: LINK_SOCIAL_ACCOUNT_POWERBI,
        variables: { input }
      })

      commit('ADD_SOCIAL_ACCOUNT', [linkSocialAccountPowerBI])
      return linkSocialAccountPowerBI
    },
    async unlinkSocialAccount ({ commit }, payload) {
      const { id } = payload
      return await apolloCall({
        mutation: UNLINK_SOCIAL_ACCOUNT,
        variables: { ...payload }
      }).then(() => {
        commit('REMOVE_SOCIAL_ACCOUNT', id)
      })
    },
    async getAvailableGoogleCalendarBySocialAccountId (ctx, googleSocialAccountId) {
      return await apolloCall({
        query: GET_AVAILABLE_CALENDARS_BY_GOOGLE_SOCIAL_ACCOUNT_ID,
        variables: { googleSocialAccountId },
        errorContext: 'getAvailableGoogleCalendarBySocialAccountId'
      }).then(({getAvailableCalendarsByGoogleSocialAccountId}) => getAvailableCalendarsByGoogleSocialAccountId)
    },
    async getAvailableMicrosoftCalendarBySocialAccountId (ctx, microsoftSocialAccountId) {
      return await apolloCall({
        query: GET_AVAILABLE_CALENDARS_BY_MICROSOFT_SOCIAL_ACCOUNT_ID,
        variables: { microsoftSocialAccountId },
        errorContext: 'getAvailableMicrosoftCalendarBySocialAccountId'
      }).then(({getAvailableCalendarsByMicrosoftSocialAccountId}) => getAvailableCalendarsByMicrosoftSocialAccountId)
    },
    addAccount(_, type) {
      if (!OAUTH_URLS[type]) {
        throw new Error(`Unsupported account type: ${type}`)
      }
      openSocialLoginWindow(OAUTH_URLS[type], type)
    },
    openSocialLoginWindow(_, { url, platform, action, closeFn }) {
      openSocialLoginWindow(url, platform, action, closeFn)
    },
    async receiveMessage ({ dispatch }, { data, origin }) {
      if (origin.replace(/\/$/, '') !== process.env.VUE_APP_DEFAULT_URL.replace(/\/$/, '')) {
        return { error: true, message: 'Invalid origin' }
      }

      try {
        data = JSON.parse(data)
      } catch (e) {
        return { error: true, message: 'Invalid JSON data' }
      }

      const result = handleSocialMessage(data)
      if (!result) {
        return { error: true, message: 'Invalid message format' }
      }

      try {
        await dispatch(result.action, result.input)
        return { error: false }
      } catch (e) {
        return { error: true, message: e.message, showError: true }
      }
    }
  },
  getters: {
    socialAccounts: state => state.socialAccounts,
    socialAccountsLoading: state => state.loading.socialAccounts,
    socialTwitterAccounts: state => state.socialAccounts.filter(sa => sa.type === ACCOUNT_TYPES.TWITTER),
    socialGoogleAccounts: state => state.socialAccounts.filter(sa => sa.type === ACCOUNT_TYPES.GOOGLE),
    socialInstagramAccounts: state => state.socialAccounts.filter(sa => sa.type === ACCOUNT_TYPES.INSTAGRAM),
    socialMicrosoftAccounts: state => state.socialAccounts.filter(sa => sa.type === ACCOUNT_TYPES.MICROSOFT),
    socialDropboxAccounts: state => state.socialAccounts.filter(sa => sa.type === ACCOUNT_TYPES.DROPBOX),
    socialPowerBIAccounts: state => state.socialAccounts.filter(sa => sa.type === ACCOUNT_TYPES.POWER_BI),
    toStandard: () => toStandard
  },
  mutations: {
    SET_SOCIAL_ACCOUNTS (state, socialAccounts) {
      state.socialAccounts = socialAccounts
      state.loading.socialAccounts = false
    },
    SET_LOADING_STATUS (state, { status, key }) {
      state.loading[key] = status
    },
    ADD_SOCIAL_ACCOUNT (state, socialAccounts) {
      state.socialAccounts = [
        ...state.socialAccounts.filter(sa =>
          !socialAccounts.some(newSa => newSa.id === sa.id)
        ),
        ...socialAccounts
      ]
    },
    REMOVE_SOCIAL_ACCOUNT (state, socialAccountId) {
      state.socialAccounts = state.socialAccounts.filter(sa => socialAccountId !== sa.id)
    }
  }
}
